import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { api, formatApiError } from "@/lib/api";
import { User, KeyRound, Sparkles, ArrowUpRight, XCircle } from "lucide-react";
import ApiKeysSection from "@/components/ApiKeysSection";

export default function Profile() {
  const { user, refresh } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();

  const [name, setName] = useState(user?.name || "");
  const [nameLoading, setNameLoading] = useState(false);

  const [pwd, setPwd] = useState({ current: "", next: "" });
  const [pwdLoading, setPwdLoading] = useState(false);

  const [cancelLoading, setCancelLoading] = useState(false);

  if (!user) return null;
  const plan = user.plan || "free";
  const hasSub = ["starter", "pro", "enterprise"].includes(plan);

  const saveName = async (e) => {
    e.preventDefault();
    setNameLoading(true);
    try {
      await api.patch("/auth/me", { name });
      await refresh();
      toast.success(t.profile.name_updated);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setNameLoading(false); }
  };

  const changePwd = async (e) => {
    e.preventDefault();
    setPwdLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwd.current, new_password: pwd.next,
      });
      setPwd({ current: "", next: "" });
      toast.success(t.profile.password_updated);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setPwdLoading(false); }
  };

  const cancelSub = async () => {
    if (!window.confirm(t.profile.cancel_confirm)) return;
    setCancelLoading(true);
    try {
      await api.post("/subscription/cancel");
      await refresh();
      toast.success(t.profile.subscription_cancelled);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setCancelLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="profile-page">
      <Header />
      <main className="flex-1 max-w-4xl w-full mx-auto px-5 md:px-8 py-12 md:py-16">
        <div className="label-eyebrow mb-4">— {t.profile.title}</div>
        <h1 className="font-serif-display text-4xl md:text-5xl tracking-tighter mb-12">{t.profile.title}</h1>

        {/* Account */}
        <section className="aq-card p-8 md:p-10 mb-8" data-testid="profile-account">
          <div className="flex items-center gap-3 mb-6">
            <User size={18} className="text-[#1E3A5F]" />
            <h2 className="font-serif-display text-2xl">{t.profile.account}</h2>
          </div>
          <form onSubmit={saveName} className="space-y-5">
            <Row label={t.profile.email}>
              <div className="text-[#0A0A0A] py-2">{user.email}</div>
            </Row>
            <Row label={t.profile.name}>
              <input value={name} onChange={(e) => setName(e.target.value)} required
                className="w-full bg-transparent border-b border-[#B6B09F] focus:border-[#1E3A5F] outline-none py-2"
                data-testid="profile-name-input" />
            </Row>
            <div className="pt-2">
              <button type="submit" disabled={nameLoading} className="aq-button-primary" data-testid="profile-name-save">
                {nameLoading ? "..." : t.profile.save}
              </button>
            </div>
          </form>
        </section>

        {/* Subscription */}
        <section className="aq-card p-8 md:p-10 mb-8" data-testid="profile-subscription">
          <div className="flex items-center gap-3 mb-6">
            <Sparkles size={18} className="text-[#1E3A5F]" />
            <h2 className="font-serif-display text-2xl">{t.profile.subscription}</h2>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="label-eyebrow mb-2">{t.profile.current_plan}</div>
              <div className="font-serif-display text-4xl capitalize">{plan === "free" ? t.dashboard.free : plan}</div>
              {user.plan_expires_at && (
                <div className="mt-2 font-mono-aq text-[0.65rem] tracking-[0.22em] uppercase text-[#B6B09F]">
                  {t.dashboard.expires}: {new Date(user.plan_expires_at).toLocaleDateString(lang === "tr" ? "tr-TR" : "en-US")}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => navigate("/#pricing")} className="aq-button-ghost" data-testid="profile-renew">
                {hasSub ? t.profile.renew : t.dashboard.upgrade} <ArrowUpRight size={14} />
              </button>
              {hasSub && user.role !== "admin" && (
                <button onClick={cancelSub} disabled={cancelLoading}
                  className="aq-button-ghost !border-red-600/50 !text-red-700 hover:!bg-red-50" data-testid="profile-cancel-sub">
                  <XCircle size={14} /> {cancelLoading ? "..." : t.profile.cancel_sub}
                </button>
              )}
            </div>
          </div>
          {hasSub && user.role !== "admin" && (
            <p className="mt-6 text-xs text-[#B6B09F] leading-relaxed">{t.profile.cancel_note}</p>
          )}
          {plan === "free" && (
            <p className="mt-4 text-sm text-[#4A4A4A]">{t.profile.no_active}</p>
          )}
        </section>

        {/* Password */}
        <section className="aq-card p-8 md:p-10 mb-8" data-testid="profile-password">
          <div className="flex items-center gap-3 mb-6">
            <KeyRound size={18} className="text-[#1E3A5F]" />
            <h2 className="font-serif-display text-2xl">{t.profile.password}</h2>
          </div>
          <form onSubmit={changePwd} className="space-y-5">
            <Row label={t.profile.current_password}>
              <input type="password" required value={pwd.current}
                onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
                className="w-full bg-transparent border-b border-[#B6B09F] focus:border-[#1E3A5F] outline-none py-2"
                data-testid="profile-current-password" />
            </Row>
            <Row label={t.profile.new_password}>
              <input type="password" required minLength={6} value={pwd.next}
                onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
                className="w-full bg-transparent border-b border-[#B6B09F] focus:border-[#1E3A5F] outline-none py-2"
                data-testid="profile-new-password" />
            </Row>
            <div className="pt-2">
              <button type="submit" disabled={pwdLoading} className="aq-button-primary" data-testid="profile-change-password">
                {pwdLoading ? "..." : t.profile.change_password}
              </button>
            </div>
          </form>
        </section>

        <ApiKeysSection t={t} />
      </main>
      <Footer />
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <label className="label-eyebrow block mb-2">{label}</label>
      {children}
    </div>
  );
}
